class DateManager {
  /**
   * Convets a date string to DD/MM/YYYY, hh:mm:ss format
   * @param {Date} currentDate
   * @param {String} locale
   * @returns {String} the formatted string
   *
   */
  static formatToDate (dateString, locale = 'pt-BR') {
    if (!dateString) return '-'

    const date = new Date(dateString)

    if (Number.isNaN(date.getTime())) {
      throw new Error('Invalid date string')
    }

    const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC'
    }

    return new Intl.DateTimeFormat(locale, options)
      .format(new Date(date))
      ?.replace(',', '')
  }

  /**
   * Convets an iso date to DD/MM hh:mm format
   * @param {Date} currentDate
   * @param {String} locale
   * @returns {String} the formatted string
   *
   */
  static formatToDateTime (date, locale = 'pt-BR') {
    if (!date) return '-'
    const dateFormatted = DateManager.formatToInputDate(date, locale)
    const hoursFormatted = DateManager.formatToInputHours(date, locale)
    return `${dateFormatted} ${hoursFormatted}`
  }

  /**
   * Converts an iso date to DD/MM format
   *
   * @param {Date} currentDate
   * @param {String} locale
   * @returns {String} the formatted string
   */
  static formatToInputDate (dateStringISO, locale = 'pt-BR') {
    if (!dateStringISO) return '-'
    const newUnformattedDate = new Date(dateStringISO)

    if (Number.isNaN(newUnformattedDate.getTime())) {
      throw new Error('Invalid date string')
    }

    const month = newUnformattedDate.getMonth() + 1
    const day = newUnformattedDate.getDate()

    const formattedMonth = month.toString().padStart(2, '0')
    const formattedDay = day.toString().padStart(2, '0')

    if (locale === 'en-US') {
      return `${formattedMonth}/${formattedDay}`
    }

    return `${formattedDay}/${formattedMonth}`
  }

  /**
   * Converts an iso date to DD/MM/YYYY format
   *
   * @param {Date} currentDate
   * @param {String} locale
   * @returns {String} the formatted string
   */
  static formatToInputDateYear (dateStringISO, locale = 'pt-BR') {
    const newUnformattedDate = new Date(dateStringISO)

    if (Number.isNaN(newUnformattedDate.getTime())) {
      throw new Error('Invalid date string')
    }

    const month = newUnformattedDate.getMonth() + 1
    const day = newUnformattedDate.getDate()
    const year = newUnformattedDate.getFullYear()

    const formattedMonth = month.toString().padStart(2, '0')
    const formattedDay = day.toString().padStart(2, '0')

    if (locale === 'en-US') {
      return `${formattedMonth}/${formattedDay}/${year}`
    }

    return `${formattedDay}/${formattedMonth}/${year}`
  }

  /**
   * Converts an iso date to HH:MM format
   * @param {Date} currentDate
   * @returns {String} the formatted string
   */
  static formatToInputHours (
    dateString,
    locale = 'pt-BR',
    timezone = 'America/Sao_Paulo'
  ) {
    if (!dateString) return '-'
    const date = new Date(dateString)

    if (Number.isNaN(date.getTime())) {
      throw new Error('Invalid date string')
    }

    const options = {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit'
    }

    return date.toLocaleTimeString(locale, options)
  }

  /**
   * is same day
   * @param {String | Date} startDate
   * @param {String | Date} endDate
   * @returns Boolean
   */
  static isSameDay (startDate, endDate) {
    if (!startDate || !endDate)
      throw new Error('1st and 2nd date are required!')

    if (isNaN(new Date(startDate).getTime())) {
      throw new Error('The 1st date is not valid')
    }

    if (isNaN(new Date(endDate).getTime())) {
      throw new Error('The 2nd date is not valid')
    }

    const date1st = startDate?.split('T')?.[0]
    const date2nd = endDate?.split('T')?.[0]

    return date1st === date2nd
  }

  /**
   *
   * @param {Date} currentDate new Date default
   * @returns new Date six months ago
   */
  static sixMonthsAgo (currentDate = new Date()) {
    if (isNaN(new Date(currentDate).getTime())) {
      throw new Error('Not a valid date')
    }
    const sixMonthsAgoDate = new Date(currentDate.getTime())
    sixMonthsAgoDate.setMonth(currentDate.getMonth() - 6)
    return sixMonthsAgoDate
  }

  /**
   *
   * @param {String | Date} startDate date
   * @param {String | Date} endDate date
   * @returns Number
   */
  static diffInDate (startDate, endDate) {
    if (!startDate || !endDate)
      throw new Error('It 1st and 2nd date are required!')

    if (isNaN(new Date(startDate).getTime())) {
      throw new Error('The 1st date is not valid')
    }

    if (isNaN(new Date(endDate).getTime())) {
      throw new Error('The 2nd date is not valid')
    }

    const diffInMs = new Date(endDate) - new Date(startDate)

    const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

    return Math.round(diffInDays)
  }

  /**
   * Convert minutes to hours and minutes
   * @param {Number} minutes
   * @example 1400 to 23:20
   * @returns Object { hours, minutes }
   */
  static convertToHoursMinutes (valueMinutes = 0) {
    if (!valueMinutes) return valueMinutes

    const hours = Math.floor(valueMinutes / 60)
    const minutes = valueMinutes % 60

    return { hours, minutes }
  }

  /**
   * Convert days to years, months and days
   * @param {Number} num
   * @example 100 to years: 0, months: 3, days: 10
   * @returns Object { years, months, days }
   */
  static convertDaysToYears (num = 0) {
    if (!num) return num

    const years = Math.floor(num / 365)
    const months = Math.floor((num % 365) / 30)
    const days = Math.floor((num % 365) % 30)

    return { years, months, days }
  }

  /**
   * @description Transform seconds to minutes
   * @param secondsToParse Total value in seconds
   */
  static convertSecondsToMinutes (value = 0) {
    if (typeof value !== 'number' || value < 0) {
      throw new Error('The argument must be a non-negative integer.')
    }

    const minutes = parseInt(value / 60)
    const seconds = parseInt(value % 60)
    return `${minutes <= 9 ? '0' + minutes : minutes}:${
      seconds <= 9 ? '0' + seconds : seconds
    }`
  }

  /**
   * Convert inputed hours from "22:00:00" to "22:00"
   * @param {String} hours
   * @returns Date
   */
  static hoursFormated (hours, locale = 'pt-br') {
    if (!hours) throw new Error('Missing hours param')
    if (typeof hours !== 'string') throw new Error('hours param must be String')

    const splittedHours = hours.split(':')
    let date = new Date().setHours(...splittedHours)
    date = new Date(date).toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    })
    return date
  }

  /**
   * @description transform locale pt-BR datestring on Date
   * @param {String} localeDate DD/MM/YYYY
   * @returns Date
   */
  static parseBrazilianLocaleDateStringToDate (brazilianLocaleDateString) {
    if (!brazilianLocaleDateString) throw new Error('Missing localeDate param')
    if (typeof brazilianLocaleDateString !== 'string')
      throw new Error('localeDate param must be String')

    const [day, month, year] = brazilianLocaleDateString.split('/')
    return new Date(`${year}-${month}-${day}`)
  }

  /**
   * @description transform 01/01/2000 to 01 de jan. de 2000
   * @param {String} date DD/MM/YYYY
   * @returns Date
   */
  static formatShortDateToLong (
    dateString,
    locale = 'pt-BR',
    options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }
  ) {
    const [dia, mes, ano] = dateString.split('/').map(Number)
    const timestamp = new Date(ano, mes - 1, dia).getTime()
    return new Intl.DateTimeFormat(locale, options).format(timestamp)
  }

  /**
   * @description transform seconds in hours
   * @param {Number} seconds
   * @returns {Number} hours
   */
  static convertSecondsToHoursOnly (seconds) {
    const hours = seconds ? Math.floor(seconds / 3600) : 0
    return { hours }
  }
}

export default DateManager
